/**
 * Plant-local clock helpers. Timestamps are UTC epoch ms; the plant's IANA timezone
 * decides what "today" means.
 */

export interface LocalTime {
  /** YYYY-MM-DD in plant time. */
  date: string;
  /** Minutes after local midnight. */
  minutes: number;
}

const fmtCache = new Map<string, Intl.DateTimeFormat>();

function formatter(tz: string): Intl.DateTimeFormat {
  let f = fmtCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
    fmtCache.set(tz, f);
  }
  return f;
}

export function localTime(nowMs: number, tz: string): LocalTime {
  const parts = Object.fromEntries(
    formatter(tz)
      .formatToParts(new Date(nowMs))
      .map((p) => [p.type, p.value]),
  );
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

/** Today's date in the plant. */
export const plantToday = (nowMs: number, tz: string): string => localTime(nowMs, tz).date;

export const minutesToHHMM = (m: number): string =>
  `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;

/** "2h 05m" / "14m" style age. */
export function formatAge(ms: number): string {
  const mins = Math.max(0, Math.floor(ms / 60000));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
}
